<template>
  <div class="main-puzzle-container">
    <div
      class="puzzle-container"
      :style="puzzleContainerStyle"
    >
      <img
        class="puzzle-background"
        ref="background"
        :src="attributes.backgroundImg"
        @load="onImageLoad"/>
      <img
        v-if="layerToDisplay"
        class="layered-image"
        :src="layerToDisplay"
        :style="layeredImageStyle"/>
    </div>
    <div class="layer-selector-container">
      <div class="slide-to-left-shadow"></div>
      <div class="slide-to-right-shadow"></div>
      <div class="layer-selector">
        <img
          v-for="button in attributes.buttons"
          :key="button.key"
          :src="button.url"
          @click="clickOnButton(button)"/>
      </div>
    </div>
    <div class="post-layer-selector" ref="postLayerSelector">
    </div>
    <div
      class="button is-default"
      v-if="isFinished"
      @click="validate"
      :ref="onDisplaySuccessButton">
      {{ $t("common.congrats") }}&nbsp;<i class="fa fa-angle-right"></i>
    </div>
  </div>
</template>

<script>
import PuzzleMixin from '@/mixins/Puzzle'
import Shake from '@/external/shake'

export default {
  name: 'SceneListOfActions',
  mixins: [PuzzleMixin],
  props: ['attributes', 'number'],
  data () {
    return {
      imageWidth: 0,
      imageRatio: 1, // Ratio displayed comparing to original size (background)
      lastAction: -1,
      jump: {
        cumulativeZ: 0,
        cumulativeY: 0
      },
      shakeDetector: null
    }
  },
  mounted () {
    // Tweak to force scrolling down the puzzle
    const comp = this
    setTimeout(() => {
      comp.$refs.postLayerSelector.scrollIntoView({ behavior: 'smooth' })
    }, 500)
  },
  watch: {
    nextAction (value, previous) {
      if (value === previous || !value) {
        return
      }
      // Action changed
      if (previous && previous.shake) {
        this.shakeDetector.stop()
        window.removeEventListener('shake', this.onShake)
      }
      if (previous && previous.jump) {
        window.removeEventListener('devicemotion', this.onJumpDeviceMotion)
      }
      if (value.shake) {
        // create a new instance of shake.js.
        this.shakeDetector = new Shake({
          threshold: 10
        })

        // start listening to device motion
        this.shakeDetector.start()

        // register a shake event
        window.addEventListener('shake', this.onShake, false)
      }
      if (value.jump) {
        this.jump.cumulativeZ = 0
        this.jump.cumulativeY = 0

        window.addEventListener('devicemotion', this.onJumpDeviceMotion)
      }
    }
  },
  computed: {
    layerToDisplay () {
      if (this.lastAction < 0) {
        return this.attributes.initialLayer
      }
      return this.attributes.actions[this.lastAction].layerUrl
    },
    layeredImageStyle () {
      const res = {
        transform: 'scale(' + this.imageRatio.toString() + ')',
        'transform-origin': 'top left',
        top: (this.attributes.layerPosition.y * this.imageRatio).toString() + 'px',
        left: (this.attributes.layerPosition.x * this.imageRatio).toString() + 'px'
      }
      return res
    },
    // Puzzle container style: set the image width once loaded
    puzzleContainerStyle () {
      if (this.imageWidth === 0) {
        return {}
      }
      return {
        width: this.imageWidth.toString() + 'px'
      }
    },
    // Return true if last action has been accomplished
    isFinished () {
      return this.lastAction === this.attributes.actions.length - 1
    },
    nextAction () {
      if (this.isFinished) {
        return
      }
      return this.attributes.actions[this.lastAction + 1]
    }
  },
  methods: {
    updateImageSizes () {
      const bg = this.$refs.background
      this.imageRatio = bg.width / bg.naturalWidth
      this.imageWidth = bg.width
    },
    // On loading image, retrieve the display scale ratio
    onImageLoad () {
      this.updateImageSizes()
      setTimeout(this.updateImageSizes, 300)
    },
    onDisplaySuccessButton (el) {
      if (el) {
        this.playVictorySound()
      }
      setTimeout(function () {
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' })
        }
      }, 500)
    },
    clickOnButton (button) {
      if (!this.nextAction) {
        return
      }
      if (this.nextAction.click === button.key) {
        this.lastAction += 1
      }
    },
    onShake () {
      if (!this.nextAction) {
        return
      }
      if (this.nextAction.shake) {
        this.lastAction += 1
      }
    },
    onJump () {
      if (!this.nextAction) {
        return
      }
      if (this.nextAction.jump) {
        this.lastAction += 1
      }
    },
    onJumpDeviceMotion (evt) {
      const current = evt.acceleration
      const treshold = 20
      if (current.z > treshold) {
        this.jump.cumulativeZ += current.z
      }
      if (current.y > treshold) {
        this.jump.cumulativeY += current.y
      }
      if (this.jump.cumulativeZ > 500 || this.jump.cumulativeY > 500) {
        this.onJump()
      }
    },
    validate () {
      this.$store.dispatch('validateItem', {
        number: this.number
      })
    }
  }
}
</script>

<style scoped lang="sass">
  .puzzle-container
    margin: auto
    position: relative
    .puzzle-background
      display: block
      max-width: 90%
    img
      user-select: none

  .layer-selector-container
    position: relative
    height: 80px

    .slide-to-left-shadow, .slide-to-right-shadow
      position: absolute
      pointer-events: none
      width: 100px
      top: 0px
      bottom: 0px
    .slide-to-left-shadow
      background: linear-gradient(90deg, rgba(255, 255, 255, 0.5), rgba(0, 0, 0, 0))
    .slide-to-right-shadow
      right: 0px
      background: linear-gradient(270deg, rgba(255, 255, 255, 0.5), rgba(0, 0, 0, 0))

  .layer-selector
    display: flex
    height: 100%
    img
      display: block
      cursor: pointer
      user-select: none
      height: 100%
      width: auto
    overflow-x: scroll
    scroll-snap-type: x mandatory

  .layered-image
    position: absolute

  .post-layer-selector
    padding-top: 50px

  .button.is-default
    margin-bottom: 30px
</style>
